// Uang muka (DP) & piutang pelanggan di kasir web. Aturan SAMA dengan server (backend utils/piutang.js) dan aplikasi HP:
//  - pelanggan wajib terdaftar,
//  - uang muka > 0 dan < tagihan (sama/lebih = pembayaran biasa), tanpa kembalian,
//  - bagian tunai paling banyak satu, non-tunai wajib memilih metode, maksimal 5 bagian,
//  - jatuh tempo WAJIB (YYYY-MM-DD), bawaan H+7, tidak boleh sebelum hari ini.
// Server tetap memeriksa ulang terhadap total yang dihitungnya.
export const MAKS_BAGIAN_DP = 5;
const TOLERANSI = 0.01;
const rp = (v) => `Rp ${Math.round(Number(v) || 0).toLocaleString('id-ID')}`;
const angka = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

export const HARI_JATUH_TEMPO = 7;
const dua = (n) => String(n).padStart(2, '0');
/** Tanggal lokal "YYYY-MM-DD", digeser `hari` hari dari sekarang. */
export const tanggalLokal = (hari = 0, dari = new Date()) => {
  const d = new Date(dari.getFullYear(), dari.getMonth(), dari.getDate() + hari);
  return `${d.getFullYear()}-${dua(d.getMonth() + 1)}-${dua(d.getDate())}`;
};

export const dpAwal = (pelanggan = null) => ({ pelanggan, non: [{ uuid: '', nominal: '' }], tunai: '', jatuhTempo: tanggalLokal(HARI_JATUH_TEMPO) });

const tanggalValid = (s) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
};

/** "2026-10-31" -> "31 Okt 2026" (tanpa zona waktu, supaya tanggal tidak bergeser). */
export const tanggalIndo = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ''));
  if (!m) return '';
  const bulan = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
  return `${Number(m[3])} ${bulan[Number(m[2]) - 1]} ${m[1]}`;
};

/** Baris struk uang muka: tiap pembayaran, lalu dibayar, sisa tagihan, dan jatuh tempo. */
export const barisDp = (bagian, total, jatuhTempo) => {
  const dibayar = (bagian || []).reduce((a, b) => a + angka(b.nominal), 0);
  const baris = (bagian || []).map((b) => `${b.metode === 'cash' ? 'Tunai' : b.nonCashMethodName || 'Non-tunai'}: ${rp(b.nominal)}`);
  baris.push(`Dibayar (DP): ${rp(dibayar)}`);
  baris.push(`SISA TAGIHAN: ${rp(Math.max(0, total - dibayar))}`);
  if (jatuhTempo) baris.push(`Jatuh tempo: ${tanggalIndo(jatuhTempo)}`);
  return baris;
};

/**
 * Hitung & periksa rencana uang muka.
 * @param total tagihan akhir (setelah promo & PPN)
 * @param dp { pelanggan, non:[{uuid,nominal}], tunai, jatuhTempo }
 * @param metodeNonTunai daftar metode non-tunai [{ uuid, name }]
 * @returns { galat, sah, dibayar, sisa, payload, baris }
 */
export const hitungDp = (total, dp, metodeNonTunai) => {
  const tagihan = Number(total) || 0;
  const non = (dp?.non || [])
    .filter((b) => b.uuid || angka(b.nominal) > 0)
    .map((b) => ({ uuid: b.uuid, nama: (metodeNonTunai.find((m) => m.uuid === b.uuid) || {}).name || '', nominal: angka(b.nominal) }));
  const tunai = angka(dp?.tunai);
  const bagianJumlah = non.length + (tunai > 0 ? 1 : 0);
  const dibayar = non.reduce((a, b) => a + b.nominal, 0) + (tunai > 0 ? tunai : 0);
  const jatuhTempo = String(dp?.jatuhTempo || '').trim();

  let galat = null;
  if (!dp?.pelanggan?.uuid) galat = 'Pilih pelanggan untuk uang muka';
  else if (bagianJumlah < 1 || dibayar <= 0) galat = 'Isi nominal uang muka';
  else if (bagianJumlah > MAKS_BAGIAN_DP) galat = `Maksimal ${MAKS_BAGIAN_DP} bagian pembayaran`;
  else if (non.some((b) => b.nominal <= 0)) galat = 'Nominal non-tunai harus lebih dari 0';
  else if (non.some((b) => !b.nama)) galat = 'Pilih metode untuk setiap bagian non-tunai';
  else if (dibayar >= tagihan - TOLERANSI) galat = `Uang muka harus kurang dari tagihan (${rp(tagihan)}); untuk bayar penuh gunakan pembayaran biasa`;
  else if (!jatuhTempo) galat = 'Jatuh tempo wajib diisi';
  else if (!tanggalValid(jatuhTempo)) galat = 'Jatuh tempo tidak valid';
  else if (jatuhTempo < tanggalLokal(0)) galat = 'Jatuh tempo tidak boleh sebelum hari ini';

  const bagian = [
    ...(tunai > 0 ? [{ metode: 'cash', nominal: tunai }] : []),
    ...non.map((b) => ({ metode: 'noncash', nonCashMethodName: b.nama, nominal: b.nominal })),
  ];
  return {
    galat,
    sah: !galat,
    dibayar,
    sisa: Math.max(0, tagihan - dibayar),
    payload: { bagian, jatuhTempo },
    baris: barisDp(bagian, tagihan, jatuhTempo),
  };
};

/**
 * Baris struk BUKTI PEMBAYARAN piutang dari detail server (GET /piutang/transaksi/:uuid): uang muka, tiap pembayaran
 * berikutnya (dengan tanggal), total dibayar, lalu sisa tagihan atau LUNAS.
 */
export const barisBuktiPiutang = (detail) => {
  const d = detail || {};
  const baris = [];
  (d.uangMuka || []).forEach((b) => baris.push(`DP ${b.metode === 'cash' ? 'Tunai' : b.nonCashMethodName || 'Non-tunai'}: ${rp(b.nominal)}`));
  (d.pembayaran || [])
    .filter((p) => p.jenis === 'bayar')
    .forEach((p) => baris.push(`${tanggalIndo(p.tanggal)} ${p.metode === 'cash' ? 'Tunai' : p.nonCashMethodName || 'Non-tunai'}: ${rp(p.jumlah)}`));
  baris.push(`Total dibayar: ${rp(d.dibayar)}`);
  const sisa = Number(d.sisaTagihan) || 0;
  baris.push(sisa > TOLERANSI ? `SISA TAGIHAN: ${rp(sisa)}` : 'LUNAS');
  if (sisa > TOLERANSI && d.jatuhTempo) baris.push(`Jatuh tempo: ${tanggalIndo(d.jatuhTempo)}`);
  return baris;
};
